import { useState } from "react";
import { Bar } from "react-chartjs-2";
import { useGetPdfExportsFullQuery, useGetPdfExportsQuery } from "../../services/API/statisticsAPI";
import exportAllExports from "./Export/exportAllExports";
import { useTranslation } from "react-i18next";
import ChartCard from "./ChartCard";
import CsvDownloadButton from "./CsvDownloadButton";
import LegendToggleRow from "./LegendToggleRow";
import { baseOptions, colorAt, monthlyScales, YEAR_COLORS } from "./chartTheme";

const PdfExportStats = () => {
  const { t } = useTranslation(["translation", "statistics"]);
  const MAX_RESULTS = 10;
  const [hiddenYears, setHiddenYears] = useState([]);

  const toggleYear = (year) =>
    setHiddenYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );

  function getNewItem(item, foundItem = null) {
    let nItem = {
      name: item.carryingElement,
      ec7: foundItem ? foundItem.ec7 : 0,
      germany: foundItem ? foundItem.germany : 0,
      aashto: foundItem ? foundItem.aashto : 0,
      fhwa: foundItem ? foundItem.fhwa : 0,
      sum: 0,
      length: item.length,
    };

    if (item.berechnungsModus === 0)
      nItem = { ...nItem, ec7: foundItem === null ? 1 : foundItem.ec7 + 1 };

    if (item.berechnungsModus === 1)
      nItem = {
        ...nItem,
        germany: foundItem === null ? 1 : foundItem.germany + 1,
      };

    if (item.berechnungsModus === 2)
      nItem = {
        ...nItem,
        aashto: foundItem === null ? 1 : foundItem.aashto + 1,
      };

    if (item.berechnungsModus === 3)
      nItem = { ...nItem, fhwa: foundItem === null ? 1 : foundItem.fhwa + 1 };

    nItem = { ...nItem, sum: foundItem === null ? 1 : foundItem.sum + 1 };

    return nItem;
  }

  const { data: pdfExportsFullData } = useGetPdfExportsFullQuery(undefined, {
    pollingInterval: 60000,
  });

  const { data: exports } = useGetPdfExportsQuery(undefined, {
    pollingInterval: 60000,
  });

  // Derive all years present in data for the year filter row
  const availableYears = [
    ...new Set(
      (exports ?? [])
        .filter((item) => item?.exportAt)
        .map((item) => new Date(item.exportAt).getFullYear())
    ),
  ].sort((a, b) => a - b);

  const visibleYears = availableYears.filter((year) => !hiddenYears.includes(year));

  // The top-10 ranking is built only from records of visible years;
  // undated records only count while no year is toggled off
  const visibleExports = (exports ?? []).filter((item) =>
    item?.exportAt
      ? visibleYears.includes(new Date(item.exportAt).getFullYear())
      : hiddenYears.length === 0
  );

  let allpdfexports = [];

  visibleExports.map((item) => {
    let foundItem = allpdfexports.find((it) => {
      return it.name === item.carryingElement;
    });
    if (!foundItem) {
      allpdfexports.push(getNewItem(item, null));
    } else {
      let idx = allpdfexports.findIndex((x) => x.name === item.carryingElement);
      if (idx >= 0) allpdfexports[idx] = getNewItem(item, foundItem);
    }
    return null;
  });

  allpdfexports.sort((a, b) => {
    return b.sum - a.sum;
  });

  const resultTable = allpdfexports.slice(0, MAX_RESULTS);

  const series = [
    { label: "EC7", key: "ec7" },
    { label: "EC7 DIBt", key: "germany" },
    { label: "FHWA", key: "fhwa" },
    { label: "Aashto", key: "aashto" },
  ];

  // No data: keep a single placeholder category so the chart frame still renders
  const labelsTable =
    resultTable.length > 0
      ? resultTable.map((item) => item.name)
      : [t("general.no_data", { ns: "statistics" })];

  const dataTable = {
    labels: labelsTable,
    datasets: series.map((serie, index) => ({
      label: serie.label,
      data: resultTable.map((item) => item[serie.key]),
      backgroundColor: colorAt(index),
      borderWidth: 0,
      maxBarThickness: 28,
      stack: "Stack 0",
    })),
  };

  const base = baseOptions();
  const scales = monthlyScales();
  const optionsTable = {
    ...base,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: { ...scales.x, stacked: true },
      y: { ...scales.y, stacked: true },
    },
  };

  return (
    <ChartCard
      title={t("exports.PDF-Exports", { ns: "statistics" })}
      action={
        <CsvDownloadButton
          data={exportAllExports.pdfExportFull(pdfExportsFullData ?? [])}
          headers={exportAllExports.pdfExportHeadersFull()}
          filename={"stats_pdf_exports.csv"}
        />
      }
      footer={
        availableYears.length > 0 && (
          <LegendToggleRow
            items={availableYears.map((year) => ({
              key: year,
              label: year.toString(),
              color:
                year === Math.max(...availableYears) ? YEAR_COLORS.current : YEAR_COLORS.previous,
              active: !hiddenYears.includes(year),
            }))}
            onToggle={toggleYear}
          />
        )
      }
    >
      <Bar options={optionsTable} data={dataTable} />
    </ChartCard>
  );
};

export default PdfExportStats;
